export enum NotificationType {
  FRIEND_INVITATION = 'friend_invitation',
  GROUP_INVITATION = 'group_invitation',
  CARPOOL_REQUEST = 'carpool_request',
  SHOPPING_REMINDER = 'shopping_reminder',
  ACTIVITY_UPDATE = 'activity_update',
  RENTAL_REQUEST = 'rental_request',
  LEDGER_REMINDER = 'ledger_reminder',
  TODO_REMINDER = 'todo_reminder',
  MARKET_UPDATE = 'market_update',
  TRAVEL_PLAN = 'travel_plan',
  DINING_PLAN = 'dining_plan',
  SYSTEM_MESSAGE = 'system_message',
}

export enum NotificationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
}

export interface PushNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data?: Record<string, any>;
  priority: NotificationPriority;
  timestamp: number;
  read: boolean;
  userId?: string;
  groupId?: string;
  moduleType?: string;
}

export interface NotificationPermissions {
  enabled: boolean;
  pushToken?: string;
  lastChecked: number;
  settings: Record<NotificationType, boolean>;
}

type Listener = (notifications: PushNotification[]) => void;

const DEFAULT_SETTINGS = Object.values(NotificationType).reduce<Record<NotificationType, boolean>>((acc, type) => {
  acc[type as NotificationType] = true;
  return acc;
}, {} as Record<NotificationType, boolean>);

class NotificationService {
  private static instance: NotificationService;
  private notifications: PushNotification[] = [];
  private permissions: NotificationPermissions = {
    enabled: true,
    lastChecked: Date.now(),
    settings: { ...DEFAULT_SETTINGS },
  };
  private listeners: Listener[] = [];

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  async initialize(): Promise<void> {
    // Web implementation does not require native hooks.
    return;
  }

  async checkPermissions(): Promise<boolean> {
    this.permissions.lastChecked = Date.now();
    return this.permissions.enabled;
  }

  async requestNotificationPermission(type: NotificationType): Promise<boolean> {
    this.permissions.enabled = true;
    this.permissions.settings[type] = true;
    return true;
  }

  async sendLocalNotification(notification: Omit<PushNotification, 'id' | 'timestamp' | 'read'>): Promise<void> {
    const entry: PushNotification = {
      ...notification,
      id: `web_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      timestamp: Date.now(),
      read: false,
    };

    this.notifications.unshift(entry);
    this.notifyListeners();
  }

  async reloadNotifications(): Promise<void> {
    // No-op for web fallback; notifications are kept in memory.
    return;
  }

  async markAsRead(notificationId: string): Promise<void> {
    const target = this.notifications.find((item) => item.id === notificationId);
    if (target) {
      target.read = true;
      this.notifyListeners();
    }
  }

  async markAllAsRead(): Promise<void> {
    this.notifications.forEach((notification) => {
      notification.read = true;
    });
    this.notifyListeners();
  }

  async deleteNotification(notificationId: string): Promise<void> {
    this.notifications = this.notifications.filter((item) => item.id !== notificationId);
    this.notifyListeners();
  }

  async clearAllNotifications(): Promise<void> {
    this.notifications = [];
    this.notifyListeners();
  }

  async updateNotificationSettings(type: NotificationType, enabled: boolean): Promise<void> {
    this.permissions.settings[type] = enabled;
  }

  getNotifications(): PushNotification[] {
    return [...this.notifications];
  }

  getUnreadCount(): number {
    return this.notifications.filter((item) => !item.read).length;
  }

  getPermissions(): NotificationPermissions {
    return { ...this.permissions, settings: { ...this.permissions.settings } };
  }

  addListener(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((entry) => entry !== listener);
    };
  }

  getFCMToken(): string | null {
    return null;
  }

  async checkSystemPermissions(): Promise<boolean> {
    return this.permissions.enabled;
  }

  private notifyListeners() {
    const snapshot = this.getNotifications();
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (error) {
        console.error('[NotificationService:web] listener error', error);
      }
    });
  }
}

export default NotificationService;
